/**
 * Published additionalFieldsJSON shapes (import accepts all; export uses array form):
 * - Preferred: `[{ attributeName, pdfLocation: [rect, ...] }]`
 * - Legacy map: `{ [attributeName]: { pdfLocation: [...] } }` or `{ pdfLocations: [...] }`
 * - Legacy array: `[{ attributeName, pdfLocation: rect | rect[] }]`
 *
 * `pdfLocation` is always an **array** on export (even for a single rect).
 */

export function flattenAdditionalPdfRects(pl) {
  if (Array.isArray(pl)) return pl.filter((r) => r != null);
  if (pl != null && typeof pl === "object") return [pl];
  return [];
}

/** Normalize any published/import payload to [{ attributeName, pdfLocation: rect[] }]. */
export function normalizeAdditionalFieldsJSONEntries(rawAdditional) {
  if (rawAdditional == null) return [];

  if (Array.isArray(rawAdditional)) {
    return rawAdditional.map((entry) => ({
      attributeName: entry?.attributeName ?? "",
      pdfLocation: flattenAdditionalPdfRects(
        entry?.pdfLocation ?? entry?.pdfLocations,
      ),
    }));
  }

  if (typeof rawAdditional === "object") {
    return Object.entries(rawAdditional).map(([attributeName, val]) => {
      if (val && typeof val === "object" && !Array.isArray(val)) {
        return {
          attributeName,
          pdfLocation: flattenAdditionalPdfRects(val.pdfLocation ?? val.pdfLocations),
        };
      }
      return {
        attributeName,
        pdfLocation: flattenAdditionalPdfRects(val),
      };
    });
  }

  return [];
}

/**
 * Coerce any shape to export array: [{ attributeName, pdfLocation: [...] }].
 */
export function coerceAdditionalFieldsJSONPublished(rawOrBuilt) {
  return normalizeAdditionalFieldsJSONEntries(rawOrBuilt)
    .filter((e) => (e.attributeName ?? "").trim() !== "")
    .map(({ attributeName, pdfLocation }) => ({
      attributeName,
      pdfLocation,
    }));
}

/**
 * @param {Array} additionalFields - client rows
 * @param {(item) => object} packPdfLocation - maps row to one rect or rect[]
 */
export function buildAdditionalFieldsJSONPublished(additionalFields, packPdfLocation) {
  const groups = new Map();
  const order = [];

  for (const item of additionalFields || []) {
    const name = String(item?.attributeName ?? "").trim();
    if (!name) continue;
    const rects = flattenAdditionalPdfRects(packPdfLocation(item));
    if (!groups.has(name)) {
      groups.set(name, []);
      order.push(name);
    }
    groups.get(name).push(...rects);
  }

  return order.map((attributeName) => ({
    attributeName,
    pdfLocation: groups.get(attributeName) ?? [],
  }));
}

/** @deprecated alias — map shape; use buildAdditionalFieldsJSONPublished for export. */
export function buildAdditionalFieldsJSONMap(additionalFields, packPdfLocation) {
  const out = {};
  for (const { attributeName, pdfLocation } of buildAdditionalFieldsJSONPublished(
    additionalFields,
    packPdfLocation,
  )) {
    out[attributeName] = { pdfLocation };
  }
  return out;
}
